/**
 * Grouped status view for branching lifecycles (bank statements, PSP matching).
 *
 * Instead of a linear flowchart, shows items grouped by their current status
 * with count and action hint per group.
 */

import { Badge } from '@ophelos/ui/components/badge';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent, CardHeader } from '@ophelos/ui/components/card';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getLifecycleById } from './reconciliationLifecycles';

export interface StatusGroupViewProps {
  lifecycleId: string;
  /** Count of items per status (e.g. { pending: 2, classified: 1 }). */
  statusCounts: Record<string, number>;
  /** Label for the "Proceed" link. */
  proceedLabel?: string;
  /** Link for the "Proceed" button. */
  proceedHref?: string;
}

export function StatusGroupView({ lifecycleId, statusCounts, proceedLabel, proceedHref }: StatusGroupViewProps) {
  const lifecycle = getLifecycleById(lifecycleId);
  if (!lifecycle) return null;
  const counts = statusCounts ?? {};

  const groups = lifecycle.steps
    .filter((step) => step.needs_manual_review && (counts[step.id] ?? 0) > 0)
    .map((step) => ({
      stepId: step.id,
      label: step.label,
      count: counts[step.id] ?? 0,
      action: lifecycle.statusActions?.[step.id] ?? '',
    }));

  if (groups.length === 0) return null;

  return (
    <Card>
      <CardHeader>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{lifecycle.label}</p>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {groups.map((group) => (
            <div key={group.stepId} className="stack gap-2 text-xs">
              <div className="flex items-center gap-1">
                <Badge variant="warning" size="number">
                  {group.count}
                </Badge>
                <span className="font-medium">{group.label}</span>
              </div>
              {group.action && (
                <div className="flex items-center gap-1">
                  <ArrowRight className="size-3 text-muted-foreground shrink-0" />
                  <span className="text-muted-foreground">{group.action}</span>
                </div>
              )}
            </div>
          ))}
        </div>
        {proceedHref && (
          <div className="mt-3 pt-2 ">
            <Button variant="default" size="sm" asChild>
              <Link to={proceedHref}>
                {proceedLabel ?? 'Proceed'}
                <ExternalLink className="size-3.5 ml-1 shrink-0" />
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
